/**
 * @fileoverview Pill badge showing a student's recording-consent status.
 */

import { ShieldCheck, ShieldX, ShieldAlert, Clock, School } from "lucide-react";
import { PROGRAM_ROSTER_LABEL } from "@/lib/consent";

// "Studio Quiet" palette: success #7FB78C, accent yellow #F4CF08, danger #E26B5C.
const STATUS_CONFIG = {
  granted: {
    label: "Granted",
    Icon: ShieldCheck,
    classes: "bg-[var(--state-success-dim)] border-[var(--state-success-border)] text-[var(--state-success)]",
  },
  requested: {
    label: "Pending",
    Icon: Clock,
    classes: "bg-[var(--accent-yellow-dim)] border-[var(--accent-yellow-border)] text-[var(--accent-yellow-text)]",
  },
  declined: {
    label: "Declined",
    Icon: ShieldX,
    classes: "bg-[var(--state-danger-dim)] border-[var(--state-danger-border)] text-[var(--state-danger)]",
  },
  revoked: {
    label: "Revoked",
    Icon: ShieldX,
    classes: "bg-[var(--state-danger-dim)] border-[var(--state-danger-border)] text-[var(--state-danger)]",
  },
};

// Legacy students (consent_status "none", null, or missing) are blocked from
// recording, so the fallback renders in the blocked color.
const NOT_REQUESTED = {
  label: "Not requested",
  Icon: ShieldAlert,
  classes: "bg-[var(--state-danger-dim)] border-[var(--state-danger-border)] text-[var(--state-danger)]",
};

// A program roster's student with no consent on file. The instructor cannot
// ask (the organization collects it), so the pill says where the student came
// from, in the cool accent, rather than a red "Not requested" that reads as
// the instructor's to fix.
const PROGRAM_ROSTER = {
  label: PROGRAM_ROSTER_LABEL,
  Icon: School,
  classes: "bg-[var(--accent-cool-dim)] border-[var(--accent-cool-dim)] text-[var(--accent-cool)]",
};

/**
 * Consent status pill.
 * @param {object} props
 * @param {string} [props.status] - consent_status value; anything unknown renders "Not requested".
 * @param {boolean} [props.programRoster] - On a program's roster: "Program roster" replaces "Not requested".
 * @param {string} [props.className] - Extra classes appended to the pill.
 */
export const ConsentBadge = ({ status, programRoster = false, className = "" }) => {
  const { label, Icon, classes } = STATUS_CONFIG[status] || (programRoster ? PROGRAM_ROSTER : NOT_REQUESTED);
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 border rounded-full text-xs font-medium whitespace-nowrap ${classes} ${className}`}
      data-testid="consent-badge"
    >
      <Icon className="w-3.5 h-3.5" /> {label}
    </span>
  );
};
